import apiCall from './api';

/**
 * ✅ ATELIER: Service pour la gestion du catalogue de modèles (atelier de couture).
 *
 * ✅ MODIFIÉ:
 *   - Suppression du concept "image du tissu" (uploadFabricImage retiré).
 *   - Ajout du support de la galerie d'images :
 *       - uploadModelImage : ajoute une image (n'écrase plus la précédente)
 *       - getModelImages, deleteModelImage, setPrimaryImage : nouvelles méthodes
 */
const modelService = {

  /**
   * Récupérer tous les modèles actifs de la boutique de l'utilisateur connecté.
   * @param {string} searchTerm - Recherche textuelle optionnelle
   */
  async getModels(searchTerm = '') {
    try {
      console.log('🧵 Récupération modèles atelier...', { searchTerm });

      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await apiCall.get('/atelier/models', params);

      if (response.success) {
        const models = Array.isArray(response.data) ? response.data : [];
        console.log(`✅ ${models.length} modèle(s) récupéré(s)`);
        return {
          success: true,
          data: models,
          message: response.message
        };
      }

      return {
        success: false,
        data: [],
        message: response.message || 'Erreur lors de la récupération'
      };

    } catch (error) {
      console.error('❌ Erreur récupération modèles:', error);
      return {
        success: false,
        data: [],
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupérer un modèle par son ID.
   */
  async getModelById(modelId) {
    try {
      console.log('🔍 Récupération modèle:', modelId);

      const response = await apiCall.get(`/atelier/models/${modelId}`);
      return response;

    } catch (error) {
      console.error('❌ Erreur récupération modèle:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Créer un nouveau modèle dans le catalogue.
   *
   * ✅ MODIFIÉ: champ fabricImageUrl retiré du payload.
   */
  async createModel(modelData) {
    try {
      console.log('➕ Création modèle:', modelData.name);

      // Validation côté frontend
      if (!modelData.name || !modelData.name.trim()) {
        return { success: false, message: 'Le nom du modèle est obligatoire' };
      }

      const payload = {
        name: modelData.name.trim(),
        description: modelData.description?.trim() || null,
        basePrice: modelData.basePrice ? parseFloat(modelData.basePrice) : 0,
        estimatedDays: modelData.estimatedDays ? parseInt(modelData.estimatedDays) : 7,
        imageUrl: modelData.imageUrl || null,
        category: modelData.category?.trim() || null
      };

      const response = await apiCall.post('/atelier/models', payload);

      if (response.success) {
        console.log('✅ Modèle créé:', response.data?.id);
      }

      return response;

    } catch (error) {
      console.error('❌ Erreur création modèle:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Mettre à jour un modèle existant.
   *
   * ✅ MODIFIÉ: champ fabricImageUrl retiré du payload.
   */
  async updateModel(modelId, modelData) {
    try {
      console.log('📝 Mise à jour modèle:', modelId);

      if (!modelData.name || !modelData.name.trim()) {
        return { success: false, message: 'Le nom du modèle est obligatoire' };
      }

      const payload = {
        name: modelData.name.trim(),
        description: modelData.description?.trim() || null,
        basePrice: modelData.basePrice ? parseFloat(modelData.basePrice) : 0,
        estimatedDays: modelData.estimatedDays ? parseInt(modelData.estimatedDays) : 7,
        imageUrl: modelData.imageUrl || null,
        category: modelData.category?.trim() || null
      };

      const response = await apiCall.put(`/atelier/models/${modelId}`, payload);
      return response;

    } catch (error) {
      console.error('❌ Erreur mise à jour modèle:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Supprimer un modèle (soft delete).
   */
  async deleteModel(modelId) {
    try {
      console.log('🗑️ Suppression modèle:', modelId);

      const response = await apiCall.delete(`/atelier/models/${modelId}`);
      return response;

    } catch (error) {
      console.error('❌ Erreur suppression modèle:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupérer la liste des catégories distinctes utilisées.
   */
  async getCategories() {
    try {
      const response = await apiCall.get('/atelier/models/categories');

      if (response.success) {
        return {
          success: true,
          data: Array.isArray(response.data) ? response.data : []
        };
      }

      return { success: false, data: [] };

    } catch (error) {
      console.error('❌ Erreur récupération catégories:', error);
      return { success: false, data: [] };
    }
  },

  // ============================================================
  // ===== GALERIE D'IMAGES DU MODÈLE =====
  // ============================================================

  /**
   * Validation locale d'un fichier image avant upload.
   * Mêmes règles que côté serveur (5MB max, JPG/PNG/WebP).
   */
  validateImageFile(file) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return {
        isValid: false,
        message: 'Type de fichier non supporté. Utilisez JPG, PNG ou WebP'
      };
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        message: 'La taille du fichier ne doit pas dépasser 5MB'
      };
    }
    return { isValid: true };
  },

  /**
   * ✅ MODIFIÉ: Ajouter une image à la galerie d'un modèle.
   *
   * ⚠️ Comportement changé : cette méthode AJOUTE désormais une image à la galerie
   * au lieu de remplacer l'image principale.
   *
   * - Si c'est la première image du modèle → marquée principale automatiquement
   * - Sinon, ajoutée en fin de galerie sans toucher à l'image principale actuelle
   *
   * Réponse :
   *   - response.data = { id, imageUrl, displayOrder, isPrimary, createdAt, updatedAt }
   *
   * Note rétrocompatibilité : pour faciliter la transition, on expose aussi
   * response.data.url (alias de imageUrl) au cas où d'anciens appelants l'utiliseraient.
   */
  async uploadModelImage(modelId, imageFile) {
    try {
      console.log('🖼️ Ajout image galerie modèle:', modelId, imageFile?.name);

      // Validation locale
      const validation = this.validateImageFile(imageFile);
      if (!validation.isValid) {
        return { success: false, message: validation.message };
      }

      // Préparation FormData
      const formData = new FormData();
      formData.append('image', imageFile);

      // Appel API (l'URL n'a pas changé pour préserver la compatibilité réseau)
      const response = await apiCall.upload(
        `/atelier/models/${modelId}/upload-image`,
        formData
      );

      if (response.success) {
        console.log('✅ Image ajoutée à la galerie:', response.data);

        // Alias rétrocompatibilité (anciens appelants attendaient une string url)
        const imageDto = response.data || {};
        return {
          success: true,
          data: {
            ...imageDto,
            // alias : avant, response.data était directement l'URL
            url: imageDto.imageUrl
          },
          message: response.message || 'Image ajoutée avec succès'
        };
      }

      return {
        success: false,
        message: response.message || 'Erreur lors de l\'upload'
      };

    } catch (error) {
      console.error('❌ Erreur upload image modèle:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Récupérer la galerie complète d'un modèle (triée par displayOrder).
   *
   * Note: les images sont déjà incluses dans ModelDto.images quand on récupère
   * un modèle. Cette méthode est utile pour rafraîchir uniquement la galerie
   * (par exemple après ajout/suppression sans recharger le modèle entier).
   */
  async getModelImages(modelId) {
    try {
      console.log('📷 Récupération galerie modèle:', modelId);

      const response = await apiCall.get(`/atelier/models/${modelId}/images`);

      if (response.success) {
        return {
          success: true,
          data: Array.isArray(response.data) ? response.data : [],
          message: response.message
        };
      }

      return {
        success: false,
        data: [],
        message: response.message || 'Erreur lors de la récupération'
      };

    } catch (error) {
      console.error('❌ Erreur récupération galerie:', error);
      return {
        success: false,
        data: [],
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Supprimer une image de la galerie d'un modèle.
   *
   * Si l'image supprimée était l'image principale, la première image restante
   * est promue automatiquement comme nouvelle principale (logique côté backend).
   * Le fichier physique est aussi supprimé du serveur.
   */
  async deleteModelImage(modelId, imageId) {
    try {
      console.log('🗑️ Suppression image galerie:', { modelId, imageId });

      const response = await apiCall.delete(
        `/atelier/models/${modelId}/images/${imageId}`
      );

      if (response.success) {
        console.log('✅ Image supprimée');
      }

      return response;

    } catch (error) {
      console.error('❌ Erreur suppression image:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Définir une image comme image principale du modèle.
   *
   * Démarque automatiquement toutes les autres images de la galerie,
   * marque celle-ci comme principale, et synchronise model.imageUrl.
   */
  async setPrimaryImage(modelId, imageId) {
    try {
      console.log('⭐ Définition image principale:', { modelId, imageId });

      const response = await apiCall.put(
        `/atelier/models/${modelId}/images/${imageId}/primary`
      );

      if (response.success) {
        console.log('✅ Image principale mise à jour');
      }

      return response;

    } catch (error) {
      console.error('❌ Erreur définition image principale:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Construire l'URL absolue d'une image à partir d'une URL relative.
   *
   * ✅ Aligné sur le pattern utilisé dans BoutiqueLogo.js (qui fonctionne en prod).
   *
   * Stratégie :
   *   1. URL déjà absolue (http/https) → on la retourne telle quelle
   *      (sauf cas spécial : URL pointant vers localhost à corriger en prod)
   *   2. URL relative (/api/uploads/...) → on préfixe avec l'origine du frontend
   *      (window.location.origin) pour rester sur le même domaine HTTPS
   *      Ce comportement évite les soucis de Mixed Content + CORS.
   */
  buildImageUrl(relativeUrl) {
    if (!relativeUrl) return null;

    // 1. Cas spécial : URL absolue mais pointant vers localhost en environnement prod
    //    (ancienne donnée mal stockée ou retour d'API contenant localhost)
    if (typeof window !== 'undefined' && window.location.protocol === 'https:'
        && relativeUrl.startsWith('http://localhost')) {
      // Extraire le path après le port (ex: "/api/uploads/...")
      const pathMatch = relativeUrl.match(/^http:\/\/localhost:\d+(\/.*)$/);
      if (pathMatch && pathMatch[1]) {
        return `${window.location.origin}${pathMatch[1].replace(/^\/api/, '/smartboutik/api')}`;
      }
    }

    // 2. URL déjà absolue (http://... ou https://...) → on la retourne telle quelle
    if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
      return relativeUrl;
    }

    // 3. URL relative (/api/uploads/...) :
    //    - En dev (localhost) : préfixer avec REACT_APP_API_URL (ex: http://localhost:8082)
    //    - En prod : préfixer avec window.location.origin + sous-chemin de l'app
    //      (ex: https://apps.prod.atlanticds-sn.com/smartboutik)
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      const pathname = window.location.pathname; // ex: /smartboutik/atelier/models

      // Détecter si on est dans une app servie sous un sous-chemin (ex: /smartboutik)
      // en regardant le chemin du frontend
      const appBasePath = pathname.startsWith('/smartboutik') ? '/smartboutik' : '';

      // Si on est en dev (localhost frontend), on cible le backend dev
      if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '')
          || 'http://localhost:8082';
        return `${backendUrl}${relativeUrl}`;
      }

      // En prod : même origine que le frontend, avec le sous-chemin si nécessaire
      // L'URL BDD commence déjà par /api, donc on ajoute juste l'origine + base path de l'app
      // Si l'URL relative commence par /api et qu'on a un appBasePath, on construit:
      //   https://apps.prod.atlanticds-sn.com + /smartboutik + /api/uploads/...
      return `${origin}${appBasePath}${relativeUrl}`;
    }

    // 4. Fallback (SSR ou contexte sans window) : utiliser REACT_APP_API_URL si défini
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    return `${baseUrl}${relativeUrl}`;
  },

  /**
   * ✅ NOUVEAU: Récupérer l'URL absolue de l'image principale d'un modèle.
   *
   * Cherche d'abord dans la galerie (model.images, image avec isPrimary=true),
   * puis se rabat sur model.imageUrl pour rétrocompatibilité.
   * Renvoie null si aucune image n'est disponible.
   */
  getPrimaryImageUrl(model) {
    if (!model) return null;

    // 1. Chercher l'image principale dans la galerie
    if (Array.isArray(model.images) && model.images.length > 0) {
      const primary = model.images.find(img => img.isPrimary === true);
      if (primary?.imageUrl) {
        return this.buildImageUrl(primary.imageUrl);
      }
      // Pas de primary explicite ? prendre la première
      if (model.images[0]?.imageUrl) {
        return this.buildImageUrl(model.images[0].imageUrl);
      }
    }

    // 2. Fallback sur le champ legacy imageUrl
    if (model.imageUrl) {
      return this.buildImageUrl(model.imageUrl);
    }

    return null;
  },

  // ===== UTILITAIRES D'AFFICHAGE =====

  /**
   * Formater le prix en FCFA.
   */
  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price || 0);
  },

  /**
   * Formater la durée de confection.
   */
  formatEstimatedDays(days) {
    if (!days || days <= 0) return 'Non défini';
    if (days === 1) return '1 jour';
    if (days === 7) return '1 semaine';
    if (days === 14) return '2 semaines';
    if (days === 21) return '3 semaines';
    if (days === 30) return '1 mois';
    return `${days} jours`;
  }
};

export default modelService;